// 🔋📣📥🐻 The room page's 09-29 chrome wave (SPACES_REDESIGN_BRAINSTORM_2026_09_29
// §3.3 · §3.6 · §3.8 · §5.10): where the battery mounts in every layout, who
// sees the Promote door and Share, what the Export dialog is told about the viewer, the
// `?charge=1` arrival (the `chip_in` thank-you's own helpers live in
// utils/chipIn). Pure: no React, no DOM; every function here is tested in
// utils/roomChrome.test.js, and the page's use of them is pinned in
// utils/roomChromeWiring.test.js.

// ── 🔋 the battery, in every space (§3.3) ───────────────────────────────────
// Where the one chip mounts. The first two spots are the 09-26 build, byte for
// byte (GameStepPage's own inline gates, unchanged): the desktop vitals row
// (the 💰 till pill's spot) and the phone ❤️/👎 row. The two new spots cover
// the rooms that had none:
//   'title'      desktop rooms whose masthead has NO vitals row (competitive /
//                imposter, a ported 1:1 room, the seated theater): the vitals
//                row's pill rides the title's own line (the mockup's "the
//                folded title bar keeps the chip"). Zero new height, so the
//                theater keeps its video floor. NOT the top-right cluster: the
//                app's ♫ button is fixed at right 200px beside it, which
//                leaves that cluster ~190px, already spent (measured 09-29:
//                a battery there overlapped the ♫ in competitive rooms).
//   'ported-row' a ported 1:1 room on a phone: the ❤️/👎 row's spot, holding
//                only the battery (Nina 08-29: no ❤️/👎 in a companion room).
// Never for a bot (?bot=1: the explorer fleet reads the DOM) and never on the
// Their Place page (SHOW_PORTED_PLACE, dark: it has its own slim bar).
export function powerSpot({ isMobile = false, competitive = false, imposter = false, ported = false, placeOn = false, theaterHero = false, bot = false } = {}) {
  if (bot) return null;
  if (isMobile) {
    if (!ported) return 'phone-row';
    return placeOn ? null : 'ported-row';
  }
  if (placeOn) return null;
  if (competitive || imposter || ported || theaterHero) return 'title';
  return 'vitals';
}

// ── 📣 Promote: everyone in a public room, the mod in a hidden one ──────────
// Nina 10-01: "rooms should be able to be shared/promoted by everyone as long
// as they're not private/unlisted". The status's `visibility` word
// (backend privateRoomGate.roomVisibility: 'public' | 'unlisted' | 'private')
// says which. A missing word (an older backend) or any other value = the mod
// only, today's rule, so this frontend can ship before the backend.
// Hidden rooms: the maker + mods (09-30). No web create binds a maker seat
// yet (#10d), so `maker` = the browser that made the room (isRoomMakerHere).
export function canPromote(viewerSeat, status, { admin = false, maker = false } = {}) {
  if (maker || isRoomMod(viewerSeat, status, { admin })) return true;
  return !!status && status.visibility === 'public';
}

// ── 🔗 Share: the same rule, with the opposite fallback ─────────────────────
// Nina 10-01 ("Same rule for Share"): public = everyone, a hidden room = the
// maker + mods (`maker` = this device made it, see canPromote). Done in the room
// page, never in ShareButton.js. Unlike Promote, only an explicit 'unlisted'
// or 'private' word hides it: a missing word (an older backend) keeps today's
// Share for everyone, so a frontend that lands first never takes Share away
// from a room (the House included).
const HIDDEN_VISIBILITY = Object.freeze(['unlisted', 'private']);
export function canShare(viewerSeat, status, { admin = false, maker = false } = {}) {
  if (maker || isRoomMod(viewerSeat, status, { admin })) return true;
  return !HIDDEN_VISIBILITY.includes(status ? status.visibility : undefined);
}

// ── 🏠 the maker: the browser that made the room ────────────────────────────
// 10-01 (leg 3 review): a room made on the web carries no maker seat (#10d),
// so once the status says 'unlisted' its maker would lose Share. Every web
// create stamps the device instead (build form v2, the classic form, /challenge,
// /doodle). Display only: Promote and Share make no server call, and a
// hand-set key opens nothing a visitor could not already reach.
const makerKey = (gameId) => `room_maker_${gameId}`;
export function markRoomMaker(gameId) {
  if (!gameId) return;
  try { window.localStorage.setItem(makerKey(gameId), '1'); } catch (e) { /* storage off: the mod rule still applies */ }
}
export function isRoomMakerHere(gameId) {
  if (!gameId) return false;
  try { return window.localStorage.getItem(makerKey(gameId)) === '1'; } catch (e) { return false; }
}

// ── 📣 the room's mod (§3.8) ────────────────────────────────────────────────
// There are no accounts: the creator's seat on THIS device is the mod. That is
// the seat the space was created with (the status's owner_player_id, else its
// human_player_identifier: portedLoader.portedOwnerSeatOf's order, the same
// binding the backend's mod check reads first), compared with the seat this
// browser holds a secret for (the live seat, else the one it created, so a
// creator who is spectating still counts). An admin browser (Nina's /admin
// key) counts too, so the House can be promoted.
// ⚠️ NOT the composer palette's isSpaceMod: that prop also passes
// `humanPlayerId === createdPlayerIdRef.current`, and every room page seats
// its visitor on load, so it is true for every seated visitor (the backend
// refuses their mod commands). In a hidden room Promote is the mod's alone.
// A space made before the binding (no stamp) has no frontend-provable mod:
// no Promote door there unless it is public (Share still is, unless hidden).
export function isRoomMod(viewerSeat, status, { admin = false } = {}) {
  if (admin) return true;
  if (typeof viewerSeat !== 'string' || !viewerSeat || !status) return false;
  const owner = status.owner_player_id || status.human_player_identifier;
  return typeof owner === 'string' && !!owner && viewerSeat === owner;
}

// ── 🛡️ 10-01 safety floor (H2): Reset, who sees the button ─────────────────
// The server's rule, client side (backend src/utils/resetGate.js): the House and
// ported homes reset with the admin key only. Everywhere else the server wants
// a seat's secret, and (R1b, Nina 10-01: "the room's RECORDED creator, else admin
// only") that seat must be the room's recorded creator: the create-time binding
// (`creator` = status.owner_player_id || status.human_player_identifier: the page
// passes portedLoader.portedOwnerSeatOf, the binding isRoomMod reads too), never
// the earliest seat. The one exception: any seat once a competitive / imposter
// game is over ("Play Again"). So: never in the House or a ported home; else an
// admin browser always; else the viewer's seat is the creator, or the room is a
// finished game and the viewer has a seat.
// 🛡️ 10-01 safety floor (review): the House and ported homes come FIRST, ahead
// of the admin key: every Reset here is one click with no confirm, and the server
// honours the admin key there (it wipes the room). Nina's admin browser saw a
// one-click Reset in the House's ⓘ and every ported home's. A deliberate admin
// reset of those rooms goes through the API (curl with the admin key), never a button.
// ⓘ The explorer's show room also refuses a non-creator after its game, but the
// status does not say which room that is: a seated stranger there still sees the
// finished game's button, and the server's refusal reaches the button's alert.
// (The explorer BOT never sees it: the page gates its own bot mode, ?bot=1.)
export function resetOffered({ house = false, ported = false, seat = null, creator = null, game = false, completed = false } = {}, { admin = false } = {}) {
  if (house || ported) return false;
  if (admin) return true;
  if (typeof seat !== 'string' || !seat) return false;
  if (typeof creator === 'string' && !!creator && seat === creator) return true;
  return !!(game && completed);
}

// The desktop header door wears its "Promote" word only where the top-left
// strip has room for it. The title block clears that strip by 240px on a wide
// desktop and 180px on a narrow one (768-1100). Measured 09-29 (review): Back +
// the labeled door ends at 198px, and Back + the comp rooms' SFX toggle + the
// labeled door ends at 243px, so a long room name that fills its first line ran
// UNDER the door (a narrow competitive room lost "Th" of its name). Everywhere
// else the door is the megaphone alone (≈36px: ends ≤177px), with its name in
// aria-label + title. ⓘ Space Details always carries the labeled door.
export function promoteDoorCompact({ narrow = false, competitive = false, imposter = false } = {}) {
  return !!(narrow || competitive || imposter);
}

// ── the sheets' accent (Export · Promote) ──────────────────────────────────
// The sheets are always dark (#12151A, RoomSheet). A room's accent can be dark
// too (the Death Note theme's near-black red): then the ON switches and the
// primary button vanish into the sheet, and Export's "Side chat" switch reads
// OFF while it is ON. Keep the room's accent only when it clears 3:1 against
// the sheet (WCAG non-text contrast); else null (RoomSheet's green).
const SHEET_BG_LUM = 0.0075; // #12151A
const lumOf = (hex) => {
  let h = hex.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export function sheetAccent(accent) {
  const a = typeof accent === 'string' ? accent.trim() : '';
  if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(a)) return null;
  return (lumOf(a) + 0.05) / (SHEET_BG_LUM + 0.05) >= 3 ? a : null;
}

// ── 📥 Export (§5.10) ───────────────────────────────────────────────────────
// Competitive / imposter ghost chat is SECRET: the dialog never offers it there.
export function isSecretGhostRoom(status) {
  return !!(status && (status.competitive_mode || status.imposter_mode));
}

// Who the dialog reads as: a seated viewer's own seat id, only when this
// browser holds that seat's secret (ExportChatDialog's reader proves the seat
// with the stored secret itself, so the secret never rides in props); nothing
// for a spectator. Half a proof is none. (With nothing, chatGameApi.getMessages
// still falls back to the seat THIS browser holds a secret for in this room,
// e.g. a competitive player the page parks in spectate: their own seat, never
// anyone else's.)
export function exportViewer(playerId, playerSecret) {
  if (typeof playerId !== 'string' || !playerId) return null;
  if (typeof playerSecret !== 'string' || !playerSecret) return null;
  return { playerId };
}

// ── 🔗 ?charge=1: a Promote link opens the battery note once on arrival ──────
export const CHARGE_PARAM = 'charge';

export function hasChargeParam(search) {
  try {
    return new URLSearchParams(search || '').get(CHARGE_PARAM) === '1';
  } catch (e) {
    return false;
  }
}

// The same URL without the charge param (every other param, the path and the
// hash kept), so a reload never reopens the note. null = nothing to strip.
export function stripChargeParam(href) {
  try {
    const url = new URL(href);
    if (!url.searchParams.has(CHARGE_PARAM)) return null;
    url.searchParams.delete(CHARGE_PARAM);
    return url.toString();
  } catch (e) {
    return null;
  }
}
